import React, { useState, useEffect } from 'react';
import { gql } from '@apollo/client';
import MaterialTable from 'material-table';
import { List, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { query } from '../../../shared/apollo/ApolloClient';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../../shared/config/snackConfig';
import { Job } from '../../../shared/services/job';
import { mainStyles } from '../../../shared/styles/common';
import OnlineMetricsJob, { MetricsListData, MetricsTableRowData } from './OnlineMetricsJob';
import {
  columns as relevanceRankingColumns,
  transformResults as relevanceRankingTransformResults,
  actions as relevanceRankingActions,
} from './RelevanceRanking';
import {
  columns as similarityRankingColumns,
  transformResults as similarityRankingTransformResults,
} from './SimilarityRanking';

const useStyles = makeStyles({
  overview: {
    padding: '10px',
    marginBottom: '10px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  title: {
    textAlign: 'center',
  },
});

const JOB_QUERY = gql`
  query job($id: Int32!) {
    job(id: $id) {
      createdAt
      startedAt
      completedAt
      options
      id
      status
      results
      type
      rating
      parentId
    }
  }
`;

type OnlineMetricsJobResultProps = RouteComponentProps<Record<string, string>>;

const OnlineMetricsJobResult: React.FC<OnlineMetricsJobResultProps> = ({ match }: OnlineMetricsJobResultProps) => {
  const classes = { ...mainStyles(), ...useStyles() };

  const { id: jobId } = match.params;

  const [job, setJob] = useState<Job>();
  const [fetchingJob, setFetchingJob] = useState<boolean>(true);
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const data = await query<Record<string, Job>>({
          query: JOB_QUERY,
          variables: {
            id: jobId,
          },
        });
        setJob(data!.job);
      } catch (error) {
        setSnackConfig(new SnackConfig({ open: true, message: JSON.stringify(error) }));
      }
      setFetchingJob(false);
    })();
  }, [jobId]);

  const getTitle = (type: string) => {
    let title = 'Unknown job type';
    switch (type) {
      case 'RELEVANCE_RANKING':
        title = 'Alarm Triaging ';
        break;
      case 'SIMILARITY_RANKING':
      case 'SIMILARITY_RANK_WITH_CLUSTERING':
        title = 'Recommendation';
        break;
      default:
        break;
    }
    return title;
  };

  const getColumns = (type: string) => {
    let columns: Record<string, unknown>[] = [];
    switch (type) {
      case 'RELEVANCE_RANKING':
        columns = relevanceRankingColumns;
        break;
      case 'SIMILARITY_RANKING':
      case 'SIMILARITY_RANK_WITH_CLUSTERING':
        columns = similarityRankingColumns;
        break;
      default:
        break;
    }
    return columns;
  };

  const getActions = (type: string) => {
    let actions: MaterialTable<MetricsTableRowData>['props']['actions'] = [];
    switch (type) {
      case 'RELEVANCE_RANKING':
        actions = relevanceRankingActions;
        break;
      default:
        break;
    }
    return actions;
  };

  const getTransformResults = (type: string) => {
    let transformResults: (job: Job) => MetricsListData = () => ({});
    switch (type) {
      case 'RELEVANCE_RANKING':
        transformResults = relevanceRankingTransformResults;
        break;
      case 'SIMILARITY_RANKING':
      case 'SIMILARITY_RANK_WITH_CLUSTERING':
        transformResults = similarityRankingTransformResults;
        break;
      default:
        break;
    }
    return transformResults;
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.overview}>
        <SpinnerOverlay active={fetchingJob}>
          {typeof job !== 'undefined' && (
            <div>
              <h2 className={classes.title}>{getTitle(job.type)}</h2>
              <List>
                <OnlineMetricsJob
                  key={job.id}
                  job={job}
                  transformResults={getTransformResults(job.type)}
                  columns={getColumns(job.type)}
                  enableCloning={false}
                  actions={getActions(job.type)}
                />
              </List>
            </div>
          )}
        </SpinnerOverlay>
      </Paper>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default withRouter(OnlineMetricsJobResult);
